import {
  AfterViewInit,
  DestroyRef,
  Directive,
  ElementRef,
  inject,
  input,
  Renderer2,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map, skipWhile } from 'rxjs';
import { HighlightChannel } from '../service/highlight.channels';
import { HighlightService } from '../service/highlight.service';

@Directive({
  selector: '[appHighlightedText]',
})
export class HighlightedTextDirective implements AfterViewInit {
  readonly appHighlightedText = input<HighlightChannel>();

  readonly #highlightService = inject(HighlightService);

  readonly #elementRef = inject(ElementRef<HTMLElement>);

  readonly #renderer = inject(Renderer2);

  readonly #destroyRef = inject(DestroyRef);

  readonly #parser: DOMParser = new DOMParser();

  ngAfterViewInit(): void {
    const channel = this.appHighlightedText();

    const hasMathContent = this.#hasMathContent(
      this.#elementRef.nativeElement.innerHTML,
    );
    if (!hasMathContent && channel) {
      this.#highlightService
        .getSearch$(channel)
        .pipe(
          skipWhile((highlight) => !highlight),
          map((highlight) => this.#highLightContent(highlight)),
          takeUntilDestroyed(this.#destroyRef),
        )
        .subscribe((content) =>
          this.#renderer.setProperty(
            this.#elementRef.nativeElement,
            'innerHTML',
            content,
          ),
        );
    }
  }

  #highLightContent(highLight: string) {
    // This directive manipulates the innerHTML of the element it is applied to.
    // We need to remove previous highlights from the element before applying new ones.
    const originalContent = this.#sanitizedContent(
      this.#elementRef.nativeElement.innerHTML,
    );

    if (!highLight) {
      return originalContent;
    }

    if (this.#hasHtml(originalContent)) {
      return this.#highlightHtmlContent(originalContent, highLight);
    }

    return this.#highlightPlainText(originalContent, highLight);
  }

  // eslint-disable-next-line class-methods-use-this
  #hasHtml(value?: string): boolean {
    if (!value) {
      return false;
    }
    return value.indexOf('<') >= 0 && value.indexOf('</') >= 0;
  }

  // eslint-disable-next-line class-methods-use-this
  #hasMathContent(value?: string): boolean {
    if (!value) {
      return false;
    }
    return value.indexOf('tmwc-content-math') >= 0;
  }

  // eslint-disable-next-line class-methods-use-this
  #sanitizedContent(content?: string): string {
    if (!content) {
      return '';
    }
    // Remove existing highlight spans before applying new highlights
    return content.replace(
      /<span class="highlighted-text">(.*?)<\/span>/gi,
      '$1',
    );
  }

  #highlightHtmlContent(originalValue: string, highlight: string): string {
    let content = originalValue;

    const doc = this.#parser.parseFromString(originalValue, 'text/html');
    const tags = doc.querySelectorAll('*');

    tags.forEach((tag) => {
      if (tag.textContent) {
        const source = tag.textContent;
        const replaced = source.replace(
          new RegExp(highlight, 'gi'),
          '<span class="highlighted-text">$&</span>',
        );
        const originalContent = tag.outerHTML;
        const replacedContent = originalContent.replace(
          tag.textContent,
          replaced,
        );

        content = content.replace(originalContent, replacedContent);
      }
    });

    return content;
  }

  // eslint-disable-next-line class-methods-use-this
  #highlightPlainText(originalValue: string, highlight: string): string {
    return originalValue.replace(
      new RegExp(highlight, 'gi'),
      '<span class="highlighted-text">$&</span>',
    );
  }
}
